/**
 * ⭐ **THE PRE-AUTH SURFACE — the first thing every user sees, and until now the only surface in the
 * product that `shell/` did not reach.**
 *
 * ⛔ Four screens sat outside every primitive and every gate: their own centring wrapper, their own
 * card, their own error box, their own `<h1>` treatment. Three copies of the same layout drift by
 * definition — measured before this landed, the three cards already disagreed on border colour
 * (`border-border-subtle` / `border-state-warning/30` / `border-state-critical/30`), on gap (`gap-5` /
 * `gap-4` / `gap-3`) and on card width (`max-w-sm` / `max-w-md`).
 *
 * ## ⛔ NO BRANDING HOOK, DELIBERATELY
 *
 * The pre-auth surface **cannot** be per-tenant branded: the tenant is not known until after
 * authentication. A frame that accepted a `brand` prop would invite a call site that resolves the
 * tenant from a URL or an email box and renders it — leaking *"yes, that org exists here"* to an
 * unauthenticated caller. The absence of the prop is the control.
 *
 * ## Pixel provenance
 *
 * ⛔ Nothing here is copied from the HTML references. The card geometry (`max-w-sm`, `rounded-xl`,
 * `p-8`, the `h-screen` centring wrapper) is **measured in this app** — it is what `LoginPage` and
 * `ChangePasswordPage` already render, carried across so the reset is not also a visual regression on
 * the one screen every user meets. The type treatment is the app's own `PageHeading` constants, so
 * pre-auth and authed titles are one definition rather than two that agree today.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { PAGE_TITLE_CLASS } from './PageHeading';
import { dataToneClass, type DataVerdict } from './data-tone';

/**
 * ⭐⭐ **THE PAGE FRAME — the ONE literal shared by all FOUR pre-auth surfaces, which is why it is a
 * constant and not inlined in the card below.**
 *
 * The measured duplication was **page frame ×4 · card ×3**, and the two numbers differ for a real
 * reason: the loading splash (`CpAdminApp`'s `auth.isLoading` branch) and the OAuth callback's pending
 * arm are *not* cards — they are a centred spinner on the page. So the card cannot own the frame, or
 * the two surfaces that need only the frame would have to re-declare it, which is exactly the drift
 * that produced four copies.
 *
 * ⚠️ **`p-8` is in here and was absent from two of the four.** The splash and the pending arm each
 * declared `flex h-screen items-center justify-center bg-surface` with no padding; the two cards had
 * it. On a spinner the difference is invisible (nothing reaches the edge), which is precisely how a
 * frame ends up with two versions — ⛔ *a difference nobody can see is a difference nobody fixes*, and
 * it stops being invisible the first time somebody puts a sentence next to the spinner.
 */
export const AUTH_PAGE_CLASS = 'flex h-screen items-center justify-center bg-surface p-8';

/**
 * ⛔ **A LITERAL, byte-identical to `PAGE_SUB_CLASS`, and the test asserts the two are equal.**
 *
 * Why not simply import it: `panes/__tests__/prose-measure.test.ts` resolves `className={CONST}`
 * against constants declared **in the same file** only. An imported one reads to that gate as *"no
 * measure declared"*, so this component would silently become another prop-prose offender — a
 * component whose caller's sentence has no line-length bound and nothing to catch it, because the
 * literal-text scan reads zero characters through a prop.
 *
 * ⇒ the duplication is deliberate and is pinned by an equality assertion, which is the cheap half of
 * the trade. ⚠️ `78ch`, not the 100ch used for body prose: a subtitle sits directly under a large
 * title, and a measure close to the title's own width keeps the two reading as one block.
 */
export const AUTH_SUB_CLASS = 'mt-1 max-w-[78ch] text-sm text-secondary';

/**
 * ⛔ **A FAILURE INTERRUPTS; AN ADVISORY DOES NOT.**
 *
 * `role="alert"` is assertive — it cuts across whatever a screen-reader user is doing. That is right
 * for *"your password was rejected"*, which arrives in response to their own submit and blocks them,
 * and wrong for *"this link expires in 10 minutes"*, which would talk over the form they are filling
 * in. ⚠️ `aria-live` is deliberately NOT also set: each role already implies its politeness, and a
 * `role="alert"` carrying `aria-live="polite"` is two contradictory instructions in one element.
 */
const NOTICE_ROLE: Readonly<Record<DataVerdict, 'alert' | 'status'>> = {
  bad: 'alert',
  caution: 'status',
  good: 'status',
  notable: 'status',
  none: 'status',
};

export interface AuthCardFrameProps {
  readonly title: string;
  /** One line. ⛔ Never an error string — errors go to `notice`. */
  readonly subtitle?: string;
  /**
   * Move keyboard focus to the card TITLE when this card arrives.
   *
   * ⛔ **THE TITLE, NEVER AN ACTION.** A card that arrives with focus on `Approve` can be actioned by
   * a keypress the operator had already begun for the previous screen. The consent screen states the
   * same rule in the other direction (*"Approve is NOT autofocused — the operator must aim"*), and
   * the two are the same requirement seen from each end.
   *
   * ⛔ **Why focus and not a live region.** These cards REPLACE the whole page rather than appearing
   * beside it. `notice` already owns the assertive channel for a failure the operator's own submit
   * produced (`NOTICE_ROLE`), and widening that to "any card that arrives" would make every terminal
   * state assertive — the exact conflation `NOTICE_ROLE`'s docblock refuses. Moving focus announces
   * the heading, is the more reliable mechanism for a full-page swap, and leaves the tone free to be
   * honest about severity.
   *
   * ⛔ **ON ARRIVAL, NOT ON EVERY RENDER — and the mechanism is what guarantees it.** The focus call
   * lives in an effect keyed on this prop, NOT in an inline `ref={(el) => …}` callback. An inline
   * arrow ref changes identity on every render, so React detaches and re-attaches it on every update
   * and the callback fires again: pressing `Approve` sets in-flight state, the card re-renders, and
   * **focus is yanked off the pressed control back to the heading while the request is in flight.**
   * The effect form fires once per change of this prop, which is what "when this card arrives" means.
   */
  readonly autoFocusTitle?: boolean;
  /**
   * A failure or an advisory. Uses the verdict vocabulary, so pre-auth speaks the same language as the
   * rest of the product rather than inventing a fourth error box.
   */
  readonly notice?: { readonly verdict: DataVerdict; readonly message: ReactNode };
  /**
   * The card's body — a form, an action, an explanation.
   *
   * ⛔ OPTIONAL because a TERMINAL card is a dead end BY DESIGN: the consent screen's
   * invalid / expired / insufficient-permission arms deliberately carry **no retry affordance,
   * because retrying cannot help**. Requiring a child there would invite a call site to invent an
   * action that does nothing, which is strictly worse than no action — an operator who presses it
   * learns that our controls are decorative.
   */
  readonly children?: ReactNode;
  readonly footer?: ReactNode;
  readonly testId: string;
}

/**
 * Move focus to the card's title.
 *
 * ⛔ **Exported, and it takes the element as a PARAMETER** — the idiom `panes/dialog-panel.tsx:46-53`
 * records for exactly this situation. Inlining `titleRef.current?.focus()` in the effect would create
 * a `null` arm no test can enter, because a committed effect always has the element; a permanently
 * uncovered branch is precisely what this package refuses to buy with a suppression.
 */
export function focusCardTitle(el: HTMLElement | null): void {
  if (el !== null) el.focus();
}

export function AuthCardFrame({
  title,
  subtitle,
  autoFocusTitle,
  notice,
  children,
  footer,
  testId,
}: AuthCardFrameProps): React.JSX.Element {
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  /*
   * ⛔ Keyed on `autoFocusTitle` ALONE. A bare `[]` would be wrong for a card whose arm flips in
   * place, and any wider dependency list re-fires on state the operator is mid-way through — see the
   * prop's docblock for the failure that shape produces.
   *
   * ⚠️ `autoFocusTitle === true`, not `autoFocusTitle &&`: an explicit comparison keeps both arms as
   * branches the coverage bar can distinguish rather than folding `undefined` and `false` into one
   * truthiness test.
   */
  useEffect(() => {
    if (autoFocusTitle === true) focusCardTitle(titleRef.current);
  }, [autoFocusTitle]);
  return (
    <div className={AUTH_PAGE_CLASS}>
      <div
        data-testid={testId}
        className="flex w-full max-w-sm flex-col gap-5 rounded-xl border border-border-subtle bg-surface-elevated p-8"
      >
        <div>
          {/* ⛔ The card's title IS the page's `<h1>` — these screens have no `PageHeading`, so this is
              the one and only level-1 heading on a pre-auth route. */}
          <h1
            className={PAGE_TITLE_CLASS}
            data-testid={`${testId}-title`}
            ref={titleRef}
            /*
             * ⛔ `-1` UNCONDITIONALLY, and the focus call is what is conditional. `tabIndex={-1}`
             * adds nothing to the tab order, so the unconditional form costs a keyboard user
             * nothing — and it removes a branch whose only purpose would be to make the element
             * sometimes unfocusable.
             *
             * ⛔ NO `focus:outline-none`. A programmatically-focused `tabIndex={-1}` element does
             * not match `:focus-visible`, so no ring appears here anyway; suppressing `:focus`
             * outright would ALSO suppress it for a keyboard user who reaches the heading by
             * find-in-page. Removing a ring that is already invisible, at the cost of one that is
             * not, is the wrong trade.
             */
            tabIndex={-1}
          >
            {title}
          </h1>
          {subtitle === undefined ? null : (
            <p className={AUTH_SUB_CLASS} data-testid={`${testId}-subtitle`}>
              {subtitle}
            </p>
          )}
        </div>
        {notice === undefined ? null : (
          <div
            role={NOTICE_ROLE[notice.verdict]}
            data-verdict-tone={notice.verdict}
            data-testid={`${testId}-notice`}
            /* `panel`, not `badge`: this spans the card. The measure is on the same element the
               caller's message lands in, because only the receiving component knows that element. */
            className={`max-w-[100ch] rounded-lg border px-3 py-2 text-xs ${dataToneClass(notice.verdict, 'panel')}`}
          >
            {notice.message}
          </div>
        )}
        {/*
          ⚠️ `=== undefined ? null :` rather than a bare `{children}`, matching this file's own idiom
          for `subtitle`/`notice`/`footer`. It is not cosmetic: it makes the two arms *branches* the
          coverage bar can see and a test can pin, where a bare `{children}` renders `undefined`
          invisibly and there is nothing to assert.
        */}
        {children === undefined ? null : children}
        {footer === undefined ? null : (
          <div data-testid={`${testId}-footer`} className="text-2xs text-secondary">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * ⛔ **THE ONE PRE-AUTH FIELD TREATMENT — five call sites had it inline, byte-identical.**
 *
 * ⚠️ **It is deliberately NOT `FilterSection`'s `FIELD_CLASS`, and the difference is forced by the
 * container rather than free.** That one is `bg-surface-elevated` *because the filter section is
 * `bg-surface-muted`* — a field must not be lower-contrast than the chrome it sits in, or the control
 * reads as a label (see `FilterSection.tsx`). Here the container **is** `bg-surface-elevated` (the
 * card), so the same reasoning lands on the opposite token: `bg-surface`. ⇒ Two treatments, one rule.
 * Sharing the string would make one of the two surfaces wrong.
 *
 * ⚠️ Also `py-2`/`text-sm`/`rounded-lg`, not the filter bar's `py-1`/`text-xs`/`rounded`: a filter bar
 * is a dense row of controls scanned at a glance, and a credential field is the single thing on the
 * screen the user is typing into. Same reason `Button`'s `'md'` is the pre-auth size and `'sm'` is the
 * in-row one.
 *
 * ⚠️ **ONE string literal, not a `+` concatenation across two lines**, for the reason the spinner below
 * is inline at all: several gates in this package resolve `const X = '…'` with a single-literal regex
 * (`prose-measure.test.ts`'s `classConstants`) and see only the first half of a concatenation. A
 * treatment that is half-visible to a text scan is worse than one that is fully visible or fully
 * hidden, because the scan reports a confident answer about a fragment.
 */
export const AUTH_FIELD_CLASS =
  'rounded-lg border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring';

export interface AuthFieldProps {
  /** The visible label. ⛔ Wrapped `<label>`, so the association cannot be forgotten at a call site. */
  readonly label: string;
  /**
   * ⛔ **REQUIRED, and a closed union of the two the pre-auth surface has any business rendering.**
   * A `type` this component would forward blindly is how a credential field ends up as `type="text"`.
   */
  readonly type: 'email' | 'password';
  /**
   * ⛔ **REQUIRED — the password-manager contract, and the one attribute here with security weight.**
   * `current-password` vs `new-password` is what tells a manager to *offer* a saved credential versus
   * *generate and store* one. Optional-with-a-default would silently give every field whichever
   * default was chosen, and the failure is invisible in every test and every screenshot.
   *
   * ⛔ `'username webauthn'` IS A THIRD VALUE, NOT A DECORATION. WebAuthn conditional UI requires an
   * `<input>` whose `autocomplete` token list ENDS WITH `webauthn`; `@simplewebauthn/browser@13.3.0`
   * enforces this itself — `startAuthentication({ useBrowserAutofill: true })` defaults
   * `verifyBrowserAutofillInput` to `true` and THROWS when no such input is in the document
   * (`methods/startAuthentication.d.ts`). So the passkey offer appearing inside the browser's own
   * autofill popup is a property of THIS STRING, and dropping the ` webauthn` token silently
   * disables the whole feature with no error anywhere in our code.
   *
   * ⚠️ The union WIDENS rather than the prop becoming `string`: it is a closed union on purpose (see
   * above), and a fourth *named* member keeps the vocabulary iterable by a gate.
   */
  readonly autoComplete: 'username' | 'username webauthn' | 'current-password' | 'new-password';
  /**
   * The form control's `name`. ⚠️ Required, and two of the five call sites had none — a nameless
   * credential input is weaker for password managers, which key on `name` as well as `autoComplete`.
   */
  readonly name: string;
  readonly value: string;
  /** ⛔ The VALUE, not the event: no call site has a reason to reach into a DOM event. */
  readonly onChange: (value: string) => void;
  readonly testId: string;
}

/**
 * A labelled credential input.
 *
 * ⛔ **`required` is hard-coded, not a prop.** All five pre-auth fields are required and none has a
 * meaning when empty — an optional-required prop would add a branch no caller exercises, i.e. an
 * untested path in the one form every operator has to get through.
 */
export function AuthField({
  label,
  type,
  autoComplete,
  name,
  value,
  onChange,
  testId,
}: AuthFieldProps): React.JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-xs text-secondary">
      {label}
      <input
        type={type}
        name={name}
        autoComplete={autoComplete}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={AUTH_FIELD_CLASS}
      />
    </label>
  );
}

export interface AuthPendingStateProps {
  /**
   * What is being waited for. ⛔ Always a visible sentence, never a bare spinner: an unlabelled
   * spinner is indistinguishable from a hung page.
   *
   * ⚠️ Named `label` rather than `message`/`note` on purpose — `prose-measure.test.ts` treats those
   * names as sentence-bearing and would (correctly) demand a font-relative measure on the element
   * they land in. This is a two-to-three-word status, not prose, and inventing a `max-w-[100ch]` for
   * it would assert a reading property nothing here has.
   */
  readonly label: string;
  readonly testId: string;
}

/**
 * ⭐ **THE FOURTH PRE-AUTH SURFACE.** The splash is the genuine first paint of every session, it sat
 * in no corpus, and it was covered by no gate — so it drifted from its own twin (no `p-8`) without
 * anything to notice.
 *
 * ⛔ **Not an `AuthCardFrame` with a spinner inside.** A card announces *"here is a thing to read or
 * act on"*; a session restore has neither. `COMPONENT-LIBRARY.md` §4.7 names three card adopters, and
 * this is the surface that makes the frame count four while the card count stays three.
 */
export function AuthPendingState({ label, testId }: AuthPendingStateProps): React.JSX.Element {
  // Empty at the first commit, so the label arrives as a change of a live region already present.
  const [shownLabel, setShownLabel] = useState('');
  useEffect(() => {
    setShownLabel(label);
  }, [label]);
  return (
    <div className={AUTH_PAGE_CLASS} data-testid={testId}>
      <div className="flex flex-col items-center gap-3">
        {/*
          ⛔ **THE SPINNER CLASS STAYS AN INLINE LITERAL, and that is deliberate — the same trade
          `AUTH_SUB_CLASS` above documents.** `reduced-motion.test.ts` scans `className="…"` values
          only: it does NOT resolve `className={CONST}`. Hoisting this into a constant would have
          removed BOTH pre-auth spinners from that gate's view — from its offender list *and* from its
          `guardedAnimationCount()` subject floor — while looking like tidier code. ⇒ The
          de-duplication is achieved by there being ONE component, not by hoisting the string.

          ⚠️ `motion-reduce:animate-none` is the guard that gate requires, and a full-screen spinner is
          the worst place in the product to ignore a vestibular preference.

          ⚠️ `aria-hidden`: the spinner carries nothing the label does not, and an unnamed spinning box
          announced to a screen reader is noise.

          ⚠️ The in-APP loaders (`AppHost`, `AppLoader`) use lucide's `Loader2` at `text-muted` — a
          third treatment, left alone on purpose: it lives inside the authenticated shell, outside this
          workstream. Recorded so "the spinner has one definition" is read as scoped, not absolute.
        */}
        <div
          className="h-8 w-8 rounded-full border-2 border-accent border-t-transparent animate-spin motion-reduce:animate-none"
          aria-hidden
        />
        {/*
          `role="status"` with an explicit `aria-live="polite"` makes the label a polite live
          region, so a screen reader announces the wait when a page enters it after an action, such
          as the second read of the consent view after a passkey elevation. Some screen readers
          announce only a change to a live region that is already in the document, so the region
          mounts empty and takes its label in the effect after the first commit.
        */}
        <p className="text-sm text-muted" role="status" aria-live="polite" aria-atomic="true">
          {shownLabel}
        </p>
      </div>
    </div>
  );
}
